import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCheckCircle,
    FiClipboard,
    FiFileText,
    FiRefreshCw,
    FiShoppingBag,
    FiX,
} from "react-icons/fi";

import {
    useDispatch,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import useSwal from "../../../../../../hooks/useSwal";

import {
    convertQuotation,
} from "../../../../../../store/billing";

import "./DocumentConversionDialog.css";

const DOCUMENT_META = {
    PROFORMA_INVOICE: {
        title:
            "Proforma Invoice",

        icon:
            FiFileText,

        description:
            "Create an advance invoice before issuing the final sales invoice.",

        routeBase:
            "/billing/proforma-invoices",
    },

    SALE_ORDER: {
        title:
            "Sales Order",

        icon:
            FiClipboard,

        description:
            "Create a confirmed customer order for further processing.",

        routeBase:
            "/billing/sales-orders",
    },

    SALE_INVOICE: {
        title:
            "Sales Invoice",

        icon:
            FiShoppingBag,

        description:
            "Generate the final taxable sales invoice for this quotation.",

        routeBase:
            "/billing/sale-invoices",
    },

    DELIVERY_CHALLAN: {
        title:
            "Delivery Challan",

        icon:
            FiCheckCircle,

        description:
            "Create a delivery document for dispatching goods to the customer.",

        routeBase:
            "/billing/delivery-challans",
    },
};

const DEFAULT_ALLOWED_TYPES = [
    "PROFORMA_INVOICE",
    "SALE_ORDER",
    "SALE_INVOICE",
];

const getDocumentId = (
    document
) => {
    return (
        document?._id ||
        document?.id ||
        ""
    );
};

const getDocumentNumber = (
    document
) => {
    return (
        document?.documentNumber ||
        document?.quotationNumber ||
        document?.invoiceNumber ||
        document?.orderNumber ||
        document?.number ||
        "-"
    );
};

const getCustomerName = (
    document
) => {
    return (
        document?.customerSnapshot
            ?.customerName ||
        document?.customer?.customerName ||
        document?.customer?.name ||
        document?.customerName ||
        "Walk-in Customer"
    );
};

const getDocumentAmount = (
    document
) => {
    return Number(
        document?.totals?.grandTotal ||
        document?.totals?.totalAmount ||
        document?.grandTotal ||
        document?.totalAmount ||
        document?.netAmount ||
        0
    );
};

const formatCurrency = (
    value
) => {
    return new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        Number(value || 0)
    );
};

const DocumentConversionDialog = ({
    open,
    document,
    allowedTypes =
    DEFAULT_ALLOWED_TYPES,
    sourceType =
    "QUOTATION",
    onClose,
    onSuccess,
}) => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const swal =
        useSwal();

    const availableOptions =
        useMemo(() => {
            return allowedTypes
                .filter(
                    (type) =>
                        DOCUMENT_META[type]
                )
                .map(
                    (type) => ({
                        value:
                            type,

                        ...DOCUMENT_META[
                        type
                        ],
                    })
                );
        }, [
            allowedTypes,
        ]);

    const [
        selectedType,
        setSelectedType,
    ] =
        useState("");

    const [
        submitting,
        setSubmitting,
    ] =
        useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }

        setSelectedType(
            availableOptions[0]
                ?.value ||
            ""
        );

        setSubmitting(
            false
        );
    }, [
        open,
        document,
        availableOptions,
    ]);

    if (
        !open ||
        !document
    ) {
        return null;
    }

    const documentId =
        getDocumentId(
            document
        );

    const documentNumber =
        getDocumentNumber(
            document
        );

    const customerName =
        getCustomerName(
            document
        );

    const documentAmount =
        getDocumentAmount(
            document
        );

    const handleClose =
        () => {
            if (
                submitting
            ) {
                return;
            }

            if (
                typeof onClose ===
                "function"
            ) {
                onClose();
            }
        };

    const handleBackdropClick =
        (
            event
        ) => {
            if (
                event.target ===
                event.currentTarget
            ) {
                handleClose();
            }
        };

    const handleSubmit =
        async (
            event
        ) => {
            event.preventDefault();

            if (
                !selectedType ||
                !documentId
            ) {
                return;
            }

            try {
                setSubmitting(
                    true
                );

                const result = await dispatch(
                    convertQuotation({
                        documentId,
                        targetType: selectedType,
                        saveAsDraft: true,
                        payments: [],
                    })
                );

                if (
                    result?.meta
                        ?.requestStatus !==
                    "fulfilled"
                ) {
                    throw new Error(
                        result?.payload
                            ?.message ||
                        result?.payload ||
                        result?.error
                            ?.message ||
                        "Unable to convert document."
                    );
                }

                const responseData =
                    result?.payload
                        ?.data ||
                    result?.payload ||
                    {};

                const convertedDocument =
                    responseData
                        ?.convertedDocument ||
                    responseData
                        ?.document ||
                    null;

                const convertedId =
                    convertedDocument
                        ?._id ||
                    convertedDocument
                        ?.id ||
                    "";

                const selectedMeta =
                    DOCUMENT_META[
                    selectedType
                    ];

                await swal.success({
                    icon:
                        "success",

                    title:
                        "Document Converted",

                    text:
                        `${getDocumentNumber(
                            document
                        )
                        } converted to ${selectedMeta
                            ?.title ||
                        "document"
                        } successfully.`,

                    timer:
                        1500,

                    showConfirmButton:
                        false,
                });

                if (
                    typeof onSuccess ===
                    "function"
                ) {
                    onSuccess({
                        sourceDocument:
                            document,

                        convertedDocument,

                        targetType:
                            selectedType,

                        response:
                            responseData,
                    });
                }

                if (
                    convertedId &&
                    selectedMeta
                        ?.routeBase
                ) {
                    history.push(
                        `${selectedMeta.routeBase}/${convertedId}/view`
                    );

                    return;
                }

                handleClose();
            } catch (
            requestError
            ) {
                console.error(
                    `${sourceType} conversion error:`,
                    requestError
                );

                swal.error({
                    icon:
                        "error",

                    title:
                        "Conversion Failed",

                    text:
                        requestError
                            ?.response
                            ?.data
                            ?.message ||
                        requestError
                            ?.message ||
                        "Unable to convert document.",
                });
            } finally {
                setSubmitting(
                    false
                );
            }
        };

    return (
        <div
            className="billing-conversion-overlay"
            onMouseDown={
                handleBackdropClick
            }
        >
            <div
                className="billing-conversion-dialog"
                role="dialog"
                aria-modal="true"
                aria-label="Convert billing document"
                onMouseDown={(
                    event
                ) =>
                    event.stopPropagation()
                }
            >
                <div className="billing-conversion-header">
                    <div>
                        <span>
                            BILLING DOCUMENT
                        </span>

                        <h3>
                            Convert Document
                        </h3>
                    </div>

                    <button
                        type="button"
                        className="billing-conversion-close"
                        onClick={
                            handleClose
                        }
                        disabled={
                            submitting
                        }
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </div>

                <form
                    onSubmit={
                        handleSubmit
                    }
                >
                    <div className="billing-conversion-body">
                        <div className="billing-conversion-info">
                            <FiRefreshCw />

                            <div>
                                <span>
                                    {String(
                                        sourceType
                                    )
                                        .replace(
                                            /_/g,
                                            " "
                                        )
                                        .toLowerCase()
                                        .replace(
                                            /\b\w/g,
                                            (
                                                character
                                            ) =>
                                                character.toUpperCase()
                                        )}
                                </span>

                                <strong>
                                    {documentNumber}
                                </strong>

                                <small>
                                    Customer:{" "}
                                    {customerName}
                                </small>

                                <small>
                                    Amount:{" "}
                                    {formatCurrency(
                                        documentAmount
                                    )}
                                </small>
                            </div>
                        </div>

                        <div className="billing-conversion-section-heading">
                            <strong>
                                Convert To
                            </strong>

                            <span>
                                Select the document type you want to create.
                            </span>
                        </div>

                        <div className="billing-conversion-options">
                            {availableOptions.map(
                                (
                                    option
                                ) => {
                                    const Icon =
                                        option.icon ||
                                        FiFileText;

                                    const selected =
                                        selectedType ===
                                        option.value;

                                    return (
                                        <button
                                            key={
                                                option.value
                                            }
                                            type="button"
                                            className={`billing-conversion-option ${selected
                                                    ? "selected"
                                                    : ""
                                                }`}
                                            onClick={() =>
                                                setSelectedType(
                                                    option.value
                                                )
                                            }
                                            disabled={
                                                submitting
                                            }
                                        >
                                            <div className="billing-conversion-option-icon">
                                                <Icon />
                                            </div>

                                            <div className="billing-conversion-option-content">
                                                <strong>
                                                    {
                                                        option.title
                                                    }
                                                </strong>

                                                <span>
                                                    {
                                                        option.description
                                                    }
                                                </span>
                                            </div>

                                            <div className="billing-conversion-radio">
                                                <span />
                                            </div>
                                        </button>
                                    );
                                }
                            )}
                        </div>
                    </div>

                    <div className="billing-conversion-footer">
                        <button
                            type="button"
                            className="billing-conversion-cancel"
                            onClick={
                                handleClose
                            }
                            disabled={
                                submitting
                            }
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="billing-conversion-save"
                            disabled={
                                submitting ||
                                !selectedType
                            }
                        >
                            <FiRefreshCw
                                className={
                                    submitting
                                        ? "billing-conversion-spin"
                                        : ""
                                }
                            />

                            {submitting
                                ? "Converting..."
                                : "Convert Document"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DocumentConversionDialog;